import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useAuthStore } from '@/stores/authStore';
import { useLanguageStore } from '@/stores/useLanguageStore';
import { revokeToken } from '@/services/revokeToken';
import apiClient from '@/services/axios';
import { useLoading } from '@/composables/useLoading';
import { useI18n } from 'vue-i18n';
import { extractApiErrorCode, resolveApiError } from '@/utils/resolveApiError';
import type { User } from '@/types/user';
import type { NotificationTopicPreference } from '@/types/notificationTopicPreference';


export function useProfile() {
  const authStore = useAuthStore();
  const languageStore = useLanguageStore();
  const { showLoading, hideLoading } = useLoading();
  const { t } = useI18n();
  const user = ref<User | null>(null);
  const showModal = ref(false);
  const isLoading = ref(false);
  const alertMsg = ref('');

  const languageNames: Record<string, string> = {
    en: 'English',
    de: 'Deutsch',
  };

  const notificationPreferences = ref<NotificationTopicPreference[]>([]);
  const notificationsLoaded = ref(false);
  const notificationsEnabled = computed(() =>
    notificationPreferences.value.some((p) => p.isEnabled)
  );

  const fetchNotificationPreferences = async () => {
    try {
      const response = await apiClient.get('/notification-topic-preferences', {
        params: { page: 1 },
        headers: { Accept: 'application/ld+json' },
      });
      const data = response.data;
      if (data && data['hydra:member']) {
        notificationPreferences.value = data['hydra:member'] as NotificationTopicPreference[];
      } else if (Array.isArray(data)) {
        notificationPreferences.value = data as NotificationTopicPreference[];
      } else {
        notificationPreferences.value = [];
      }
    } catch (error) {
      console.error('Failed to fetch notification topic preferences:', error);
    } finally {
      notificationsLoaded.value = true;
    }
  };

  const fetchUser = async () => {
    showLoading();
    try {
      isLoading.value = true;
      const response = await apiClient.get('/users/me');
24      // If backend returns 200 but without an authenticated user payload,
      // treat as unauthorized and force logout.
      // FIX TO https://elsmartsolutions.atlassian.net/browse/LEC-168
      if (!response.data || !response.data.email || !response.data.userIdentifier) {
        authStore.logout();
        window.location.href = '/sign-in';
        return;
      }
      user.value = response.data;
    } catch (error) {
      console.error('Failed to fetch user:', error);
      authStore.logout();
      window.location.href = '/sign-in';
      //fix to https://elsmartsolutions.atlassian.net/browse/LEC-168
    } finally {
      isLoading.value = false;
      hideLoading();
    }
  };

  const logout = async () => {
    showLoading();
    try {
      authStore.logout();
      await revokeToken();
    } catch (error) {
      console.error('Error logging out:', error);
      localStorage.removeItem('access_token');
    } finally {
      hideLoading();
      window.location.href = '/sign-in';
    }
  };

  const deleteProfile = async () => {
    try {
      isLoading.value = true;
      await apiClient.delete('/users/delete');
      authStore.logout();
      window.location.href = '/';
    } catch (error) {
      showModal.value = false;
      alertMsg.value = resolveApiError(extractApiErrorCode(error), t, 'profile.delete_error');
      console.error('Error removing profile:', error);
    } finally {
      isLoading.value = false;
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      showModal.value = false;
    }
  };

  watch(showModal, (open) => {
    if (open) {
      window.addEventListener('keydown', handleKeyDown);
    } else {
      window.removeEventListener('keydown', handleKeyDown);
    }
  });

  onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeyDown);
  });

  onMounted(fetchUser);
  onMounted(fetchNotificationPreferences);

  return {
    user,
    showModal,
    isLoading,
    alertMsg,
    languageNames,
    languageStore,
    notificationPreferences,
    notificationsLoaded,
    notificationsEnabled,

    fetchUser,
    fetchNotificationPreferences,
    logout,
    deleteProfile,
  };
}
